import {
  useEffect,
  useMemo,
  useState
} from 'react';

import api, {
  formatApiError
} from '@/lib/api';

import { toast } from 'sonner';

import {
  Card,
  CardContent
} from '@/components/ui/card';

import {
  Button
} from '@/components/ui/button';

import {
  Input
} from '@/components/ui/input';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import {
  AlertCircle,
  Bell,
  CalendarClock,
  Check,
  Eye,
  Image as ImageIcon,
  Loader2,
  Megaphone,
  Pencil,
  Pin,
  Plus,
  Send,
  Trash2,
  Upload,
  Users,
  X
} from 'lucide-react';


const inicial = {
  titulo: '',
  mensagem: '',
  imagem_url: '',
  imagem_legenda: '',
  turma_ids: [],
  todas_turmas: true,
  fixado: false,
  enviar_push: true,
  publicar_em: '',
  expira_em: ''
};


const paraInputData = (valor) => {
  if (!valor) return '';

  const d = new Date(valor);

  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const pad = (v) =>
    String(v).padStart(2, '0');

  return (
    `${d.getFullYear()}-` +
    `${pad(d.getMonth() + 1)}-` +
    `${pad(d.getDate())}T` +
    `${pad(d.getHours())}:` +
    `${pad(d.getMinutes())}`
  );
};


const paraIso = (valor) => {
  if (!valor) return null;

  const d = new Date(valor);

  return Number.isNaN(d.getTime())
    ? null
    : d.toISOString();
};


const formatarData = (valor) => {
  if (!valor) return '—';

  const d = new Date(valor);

  if (Number.isNaN(d.getTime())) {
    return valor;
  }

  return d.toLocaleString(
    'pt-BR',
    {
      dateStyle: 'short',
      timeStyle: 'short'
    }
  );
};


export default function AvisosPage() {
  const [avisos, setAvisos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [dialogAberto, setDialogAberto] =
    useState(false);

  const [form, setForm] =
    useState(inicial);

  const [editando, setEditando] =
    useState(null);

  const [imagem, setImagem] =
    useState(null);

  const [preview, setPreview] =
    useState('');

  const [destinatarios, setDestinatarios] =
    useState([]);

  const [avisoDestinatarios, setAvisoDestinatarios] =
    useState(null);

  const [carregandoDestinatarios, setCarregandoDestinatarios] =
    useState(false);


  const carregar = async () => {
    setLoading(true);

    try {
      const [
        avisosRes,
        turmasRes
      ] = await Promise.all([
        api.get('/gestor/avisos'),
        api.get('/gestor/turmas')
      ]);

      setAvisos(
        avisosRes.data || []
      );

      setTurmas(
        (turmasRes.data || [])
          .filter(t => t.ativo !== false)
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    carregar();
  }, []);


  const abrirNovo = () => {
    setEditando(null);
    setForm(inicial);
    setImagem(null);
    setPreview('');
    setDialogAberto(true);
  };


  const abrirEditar = (aviso) => {
    setEditando(aviso);

    setForm({
      titulo: aviso.titulo || '',
      mensagem: aviso.mensagem || '',
      imagem_url:
        aviso.imagem_url || '',
      imagem_legenda:
        aviso.imagem_legenda || '',
      turma_ids:
        aviso.turma_ids || [],
      todas_turmas:
        Boolean(aviso.todas_turmas),
      fixado:
        Boolean(aviso.fixado),
      enviar_push:
        Boolean(aviso.enviar_push),
      publicar_em:
        paraInputData(
          aviso.publicar_em
        ),
      expira_em:
        paraInputData(
          aviso.expira_em
        )
    });

    setImagem(null);
    setPreview(
      aviso.imagem_url || ''
    );

    setDialogAberto(true);
  };


  const alternarTurma = (id) => {
    setForm(prev => {
      const selecionadas =
        prev.turma_ids.includes(id);

      return {
        ...prev,
        todas_turmas: false,
        turma_ids: selecionadas
          ? prev.turma_ids.filter(
              item => item !== id
            )
          : [
              ...prev.turma_ids,
              id
            ]
      };
    });
  };


  const selecionarTodas = () => {
    setForm(prev => ({
      ...prev,
      todas_turmas: true,
      turma_ids: []
    }));
  };


  const enviarImagem = async () => {
    if (!imagem) {
      return form.imagem_url || '';
    }

    const dados =
      new FormData();

    dados.append(
      'file',
      imagem
    );

    const resposta = await api.post(
      '/gestor/avisos/upload-imagem',
      dados,
      {
        headers: {
          'Content-Type':
            'multipart/form-data'
        }
      }
    );

    return (
      resposta.data?.imagem_url
      || ''
    );
  };


  const salvar = async () => {
    if (!form.titulo.trim()) {
      toast.error(
        'Informe o título.'
      );
      return;
    }

    if (
      !form.todas_turmas
      && !form.turma_ids.length
    ) {
      toast.error(
        'Selecione pelo menos uma turma.'
      );
      return;
    }

    setSalvando(true);

    try {
      const imagemUrl =
        await enviarImagem();

      const payload = {
        titulo: form.titulo,
        mensagem: form.mensagem,
        imagem_url: imagemUrl,
        imagem_legenda:
          form.imagem_legenda,
        turma_ids:
          form.todas_turmas
            ? []
            : form.turma_ids,
        todas_turmas:
          form.todas_turmas,
        fixado: form.fixado,
        enviar_push:
          form.enviar_push,
        publicar_em:
          paraIso(
            form.publicar_em
          ),
        expira_em:
          paraIso(
            form.expira_em
          )
      };

      if (editando) {
        await api.put(
          `/gestor/avisos/${editando.id}`,
          payload
        );

        toast.success(
          'Aviso atualizado.'
        );
      } else {
        await api.post(
          '/gestor/avisos',
          payload
        );

        toast.success(
          form.publicar_em
            ? 'Aviso agendado.'
            : 'Aviso publicado.'
        );
      }

      setDialogAberto(false);
      await carregar();
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setSalvando(false);
    }
  };


  const excluir = async (aviso) => {
    const confirmou =
      window.confirm(
        `Excluir o aviso "${aviso.titulo}"?`
      );

    if (!confirmou) return;

    try {
      await api.delete(
        `/gestor/avisos/${aviso.id}`
      );

      toast.success(
        'Aviso excluído.'
      );

      carregar();
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    }
  };


  const abrirDestinatarios =
    async (aviso) => {
      setAvisoDestinatarios(aviso);
      setDestinatarios([]);
      setCarregandoDestinatarios(true);

      try {
        const res = await api.get(
          `/gestor/avisos/${aviso.id}/destinatarios`
        );

        setDestinatarios(
          res.data || []
        );
      } catch (err) {
        toast.error(
          formatApiError(err)
        );
      } finally {
        setCarregandoDestinatarios(false);
      }
    };


  const visualizados = useMemo(
    () =>
      destinatarios.filter(
        item => item.visualizado
      ).length,
    [destinatarios]
  );


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-7 h-7 animate-spin text-[var(--cor-primaria,#007AFF)]" />
      </div>
    );
  }


  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="avisos-page"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
            Avisos
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Publique comunicados para uma ou várias turmas.
          </p>
        </div>

        <Button
          onClick={abrirNovo}
          className="bg-[var(--cor-primaria,#007AFF)]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo aviso
        </Button>
      </div>


      {!avisos.length ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-10 text-center">
            <Megaphone className="w-10 h-10 mx-auto text-zinc-600 mb-3" />

            <p className="text-white font-bold">
              Nenhum aviso publicado
            </p>

            <p className="text-sm text-zinc-500 mt-1">
              Crie o primeiro comunicado para seus alunos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {avisos.map(aviso => (
            <Card
              key={aviso.id}
              className={`bg-[#141414] overflow-hidden ${
                aviso.fixado
                  ? 'border-amber-500/30'
                  : 'border-[#27272A]'
              }`}
            >
              {aviso.imagem_url && (
                <div className="bg-black">
                  <img
                    src={aviso.imagem_url}
                    alt={aviso.imagem_legenda || aviso.titulo}
                    className="w-full max-h-72 object-cover"
                  />
                </div>
              )}

              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex gap-2 flex-wrap mb-2">
                      {aviso.fixado && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                          <Pin className="w-3 h-3" />
                          Fixado
                        </span>
                      )}

                      <span
                        className={`text-xs font-bold uppercase ${
                          aviso.status_publicacao === 'publicado'
                            ? 'text-green-400'
                            : aviso.status_publicacao === 'agendado'
                              ? 'text-blue-400'
                              : 'text-zinc-500'
                        }`}
                      >
                        {aviso.status_publicacao}
                      </span>
                    </div>

                    <h2 className="text-xl font-black font-['Barlow_Condensed'] text-white">
                      {aviso.titulo}
                    </h2>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => abrirEditar(aviso)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>

                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-400"
                      onClick={() => excluir(aviso)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {aviso.imagem_legenda && (
                  <p className="text-xs text-zinc-500 italic mt-2">
                    {aviso.imagem_legenda}
                  </p>
                )}

                {aviso.mensagem && (
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-3">
                    {aviso.mensagem}
                  </p>
                )}

                <div className="mt-4 space-y-2 text-xs text-zinc-500">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {(aviso.turma_nomes || []).join(', ')}
                  </div>

                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4" />
                    Publicação: {formatarData(aviso.publicar_em)}
                  </div>

                  {aviso.expira_em && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Expira: {formatarData(aviso.expira_em)}
                    </div>
                  )}

                  {aviso.enviar_push && (
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Push: {aviso.push_status || 'pendente'}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => abrirDestinatarios(aviso)}
                  className="mt-5 w-full rounded-lg border border-[#27272A] bg-[#0A0A0A] p-3 hover:border-[#52525B] transition-colors text-left"
                >
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-zinc-300">
                      <Eye className="w-4 h-4" />
                      Visualizações
                    </div>

                    <span className="text-sm font-bold text-white">
                      {aviso.total_visualizacoes || 0}
                      {' / '}
                      {aviso.total_destinatarios || 0}
                    </span>
                  </div>
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}


      <Dialog
        open={dialogAberto}
        onOpenChange={setDialogAberto}
      >
        <DialogContent className="bg-[#141414] border-[#27272A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editando
                ? 'Editar aviso'
                : 'Novo aviso'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <label className="text-sm text-zinc-400">
                Título *
              </label>

              <Input
                value={form.titulo}
                onChange={e =>
                  setForm({
                    ...form,
                    titulo: e.target.value
                  })
                }
                className="mt-1 bg-[#0A0A0A] border-[#27272A]"
                placeholder="Ex.: Alteração no treino de domingo"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400">
                Mensagem
              </label>

              <textarea
                rows={6}
                value={form.mensagem}
                onChange={e =>
                  setForm({
                    ...form,
                    mensagem: e.target.value
                  })
                }
                className="mt-1 w-full rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 py-2 text-sm text-white resize-y focus:outline-none focus:ring-2 focus:ring-[var(--cor-primaria,#007AFF)]"
                placeholder="Escreva o comunicado..."
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400">
                Imagem opcional
              </label>

              <label className="mt-2 border border-dashed border-[#3F3F46] rounded-lg p-5 flex flex-col items-center justify-center cursor-pointer hover:border-[#71717A]">
                <Upload className="w-6 h-6 text-zinc-500" />

                <span className="text-sm text-zinc-400 mt-2">
                  JPG, PNG ou WEBP — máximo 5 MB
                </span>

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={e => {
                    const arquivo =
                      e.target.files?.[0];

                    if (!arquivo) return;

                    setImagem(arquivo);

                    setPreview(
                      URL.createObjectURL(
                        arquivo
                      )
                    );
                  }}
                />
              </label>

              {preview && (
                <div className="relative mt-3">
                  <img
                    src={preview}
                    alt="Prévia"
                    className="rounded-lg max-h-64 w-full object-cover"
                  />

                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setImagem(null);
                      setPreview('');
                      setForm({
                        ...form,
                        imagem_url: ''
                      });
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <label className="text-sm text-zinc-400">
                Legenda da imagem
              </label>

              <Input
                value={form.imagem_legenda}
                onChange={e =>
                  setForm({
                    ...form,
                    imagem_legenda:
                      e.target.value
                  })
                }
                className="mt-1 bg-[#0A0A0A] border-[#27272A]"
                placeholder="Opcional"
              />
            </div>


            <div>
              <p className="text-sm font-bold text-white mb-3">
                Destinatários
              </p>

              <button
                type="button"
                onClick={selecionarTodas}
                className={`w-full flex items-center justify-between border rounded-lg p-3 ${
                  form.todas_turmas
                    ? 'border-[var(--cor-primaria,#007AFF)] bg-[var(--cor-primaria,#007AFF)]/10'
                    : 'border-[#27272A] bg-[#0A0A0A]'
                }`}
              >
                <span className="text-sm">
                  Todas as turmas
                </span>

                {form.todas_turmas && (
                  <Check className="w-4 h-4 text-[var(--cor-primaria,#007AFF)]" />
                )}
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {turmas.map(turma => {
                  const marcada =
                    !form.todas_turmas
                    && form.turma_ids.includes(turma.id);

                  return (
                    <button
                      type="button"
                      key={turma.id}
                      onClick={() =>
                        alternarTurma(turma.id)
                      }
                      className={`text-left border rounded-lg p-3 flex items-center justify-between gap-2 ${
                        marcada
                          ? 'border-[var(--cor-primaria,#007AFF)] bg-[var(--cor-primaria,#007AFF)]/10'
                          : 'border-[#27272A] bg-[#0A0A0A]'
                      }`}
                    >
                      <span className="text-sm">
                        {turma.nome}
                      </span>

                      {marcada && (
                        <Check className="w-4 h-4 text-[var(--cor-primaria,#007AFF)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400">
                  Agendar publicação
                </label>

                <Input
                  type="datetime-local"
                  value={form.publicar_em}
                  onChange={e =>
                    setForm({
                      ...form,
                      publicar_em:
                        e.target.value
                    })
                  }
                  className="mt-1 bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400">
                  Expirar aviso
                </label>

                <Input
                  type="datetime-local"
                  value={form.expira_em}
                  onChange={e =>
                    setForm({
                      ...form,
                      expira_em:
                        e.target.value
                    })
                  }
                  className="mt-1 bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
            </div>


            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer border border-[#27272A] rounded-lg p-3 bg-[#0A0A0A]">
                <input
                  type="checkbox"
                  checked={form.fixado}
                  onChange={e =>
                    setForm({
                      ...form,
                      fixado:
                        e.target.checked
                    })
                  }
                  className="w-4 h-4"
                />

                <Pin className="w-4 h-4 text-amber-400" />

                <span className="text-sm">
                  Fixar no topo
                </span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer border border-[#27272A] rounded-lg p-3 bg-[#0A0A0A]">
                <input
                  type="checkbox"
                  checked={form.enviar_push}
                  onChange={e =>
                    setForm({
                      ...form,
                      enviar_push:
                        e.target.checked
                    })
                  }
                  className="w-4 h-4"
                />

                <Bell className="w-4 h-4 text-blue-400" />

                <div>
                  <span className="text-sm block">
                    Enviar notificação push
                  </span>

                  <span className="text-xs text-zinc-500">
                    Será enviada quando o aviso for publicado.
                  </span>
                </div>
              </label>
            </div>


            <div className="flex justify-end gap-2 pt-3">
              <Button
                variant="outline"
                onClick={() =>
                  setDialogAberto(false)
                }
                className="border-[#3F3F46]"
              >
                Cancelar
              </Button>

              <Button
                onClick={salvar}
                disabled={salvando}
                className="bg-[var(--cor-primaria,#007AFF)]"
              >
                {salvando ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}

                {editando
                  ? 'Salvar alterações'
                  : form.publicar_em
                    ? 'Agendar aviso'
                    : 'Publicar aviso'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog
        open={Boolean(avisoDestinatarios)}
        onOpenChange={aberto => {
          if (!aberto) {
            setAvisoDestinatarios(null);
          }
        }}
      >
        <DialogContent className="bg-[#141414] border-[#27272A] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Visualizações
            </DialogTitle>
          </DialogHeader>

          {avisoDestinatarios && (
            <p className="text-sm text-zinc-500">
              {avisoDestinatarios.titulo}
            </p>
          )}

          <div className="rounded-lg bg-[#0A0A0A] border border-[#27272A] p-3 flex justify-between">
            <span className="text-sm text-zinc-400">
              Visualizaram
            </span>

            <span className="font-bold">
              {visualizados} / {destinatarios.length}
            </span>
          </div>

          {carregandoDestinatarios ? (
            <Loader2 className="w-6 h-6 animate-spin mx-auto my-8" />
          ) : (
            <div className="max-h-[55vh] overflow-y-auto space-y-2">
              {destinatarios.map(item => (
                <div
                  key={item.aluno_id}
                  className="border border-[#27272A] bg-[#0A0A0A] rounded-lg p-3 flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-zinc-300">
                    {item.nome}
                  </span>

                  {item.visualizado ? (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Visualizado
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-600">
                      Não visualizado
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
